import { useAppDispatch } from "../../../app/hooks";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { classroomStudentsSlice, retrieveClassroomStudents, selectClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { useNavigate } from "react-router";
import { selectUser } from "../../../redux/user/userSlice";
import { anyPermission, hasPermission, PermissionId } from "../../../app/permissions";
import { routeStudent } from "../../../routes/student";
import { routeClassroomStudentAdd } from "../../../routes/classroom";
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";
import { studentSlice } from "../../../redux/student/studentSlice";
import PrizaAvatarGrid from "../../../features/base/grid/PrizaAvatarGrid";
import { assertThumbnailUrl, LoadState } from "../../../redux/common";
import { useEffect, useMemo, useState } from "react";
import { isTokenReady } from "../../../features/user/UserAvatar";

import GridOnIcon from '@mui/icons-material/GridOn';
import DnsIcon from '@mui/icons-material/Dns';
import BadgedAvatar from "../../../features/base/BadgedAvatar";

interface ContentListProps {
    schoolId: string;
    classroomId: string;
}

const StudentsList = ({ schoolId, classroomId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const classroomStudents = useSelector(selectClassroomStudents);
    const user = useSelector(selectUser);
    const [table, setTable] = useState(true);

    const request = useMemo(() => ({ schoolId, classroomId }), [schoolId, classroomId]);
    useEffect(() => {
        dispatch(classroomStudentsSlice.actions.check(request));
        if (classroomStudents.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveClassroomStudents(request));
        }
    }, [request, classroomStudents]);

    const list = classroomStudents.list || [];
    const addStudent = () => {
        navigate(routeClassroomStudentAdd(schoolId, classroomId));
    };

    const mapped = list.map((item) => {
        const click = () => {
            dispatch(studentSlice.actions.clean());
            navigate(routeStudent(schoolId, item.id, classroomId));
        };
        return {
            image: assertThumbnailUrl(item.url),
            title: item.nickname || item.name,
            subtitle: item.name,
            item: item,
            click: click
        };
    });

    const perm: boolean = hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)
            || anyPermission(user.priza.info, classroomId, [ PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER ]);

    const buttons = [];
    buttons.push({
        label: table ? <DnsIcon /> : <GridOnIcon />,
        click: () => {
            setTable(!table);
        }
    });
    if (perm) {
        buttons.push({
            label: <Tooltip title="Alterar lista de alunos (adicinar ou remover)" ><AddIcon /></Tooltip>,
            click: () => { addStudent(); }
        });
    }

    const buildContent = !table ? undefined : () => {
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Aluno</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nome</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nascimento</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Matricula</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((item) => {
                        return <TableRow key={item.id} hover
                            onClick={() => {
                                dispatch(studentSlice.actions.clean());
                                navigate(routeStudent(schoolId, item.id, classroomId));
                            }}>
                            <TableCell>
                                <Stack direction="row" alignItems="center">
                                    <BadgedAvatar
                                        title={item.nickname || item.name}
                                        avatar={assertThumbnailUrl(item.url)}
                                        size="32px"
                                        marginRight={1}
                                    />
                                    {item.nickname || item.name}
                                </Stack>
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.birthDate}</TableCell>
                            <TableCell>{item.schoolNumber}</TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </>;
    };

    return <PrizaAvatarGrid list={mapped}
        toolbar={{
            label: `Alunos (${mapped.length})`,
            buttons: buttons
        }}
        state={classroomStudents.state}
        retry={() => { dispatch(classroomStudentsSlice.actions.clean()); }}
        buildContent={buildContent}
        messages={{
            empty: "Nenhum aluno associado a esta sala de aula.",
            error: "Erro ao carregar alunos."
        }}
    />;

};

export default StudentsList;